import { DO_NOT_USE_FaWrapper } from '../../../foundation/falcon/do-not-use-fa-wrapper/index.js';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { Box } from '../../../foundation/Box/index.js';
import { useRef, useState, useLayoutEffect, createContext } from 'react';
import { useGeneratedId } from '../../../../utils/index.js';
import { usePrevious } from '../../../../hooks/useTransitionState/usePrevious.js';
import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import FocusLock from 'react-focus-lock';
import { SidebarMenuCollapsibleChildrenContainer, GapFix, MenuContainer, MenuList } from './styled.js';
import { useSidebarMenu } from '../useSidebarMenu/index.js';
import { TRANSITION_DURATION } from '../config.js';
import { SidebarMenuItem } from '../SidebarMenuItem/index.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useIsMobile } from '../../../../hooks/useIsMobile.js';
import { useTransitionState } from '../../../../hooks/useTransitionState/index.js';
import { useFloatingPosition } from '../../../../hooks/useFloatingPosition/useFloatingPosition.js';
import { pxToRem } from '../../../foundation/spacing/utils.js';

var SidebarMenuCollapsibleContext = /*#__PURE__*/createContext(null);
function SidebarMenuCollapsible(props) {
  var collapsibleKey = props.collapsibleKey,
    open = props.open,
    icon = props.icon,
    label = props.label,
    activeChild = props.activeChild,
    children = props.children,
    onToggle = props.onToggle;
  var _useSidebarMenu = useSidebarMenu(),
    minimized = _useSidebarMenu.minimized;
  var previousMinimized = usePrevious(minimized);
  var childrenContainerRef = useRef(null);
  var menuContentId = useGeneratedId('menu-content');
  var isMobile = useIsMobile();
  var _useState = useState((open || activeChild) && !minimized),
    collapsibleOpen = _useState[0],
    setCollapsibleOpen = _useState[1];
  var _useState2 = useState(false),
    menuOpen = _useState2[0],
    setMenuOpen = _useState2[1];
  var menuTransitionState = useTransitionState(menuOpen, TRANSITION_DURATION);
  var menuRoot = document.body;
  var closeMenu = function closeMenu() {
    return setMenuOpen(false);
  };
  var closeCollapsible = function closeCollapsible() {
    return setCollapsibleOpen(false);
  };
  var _useFloatingPosition = useFloatingPosition({
      active: true,
      options: {
        placement: 'bottom-start'
      },
      outsideClickTrigger: function outsideClickTrigger() {
        if (menuOpen) {
          closeMenu();
        }
      }
    }),
    refs = _useFloatingPosition.refs,
    menuContainerStyle = _useFloatingPosition.wrapperStyle;
  var toggleChildren = function toggleChildren() {
    if (minimized) {
      setMenuOpen(function (prevState) {
        return !prevState;
      });
    } else {
      setCollapsibleOpen(function (prevState) {
        return !prevState;
      });
      onToggle(collapsibleKey);
    }
  };
  var openChildrenContainer = function openChildrenContainer() {
    if (!childrenContainerRef.current) {
      return;
    }
    var scrollHeight = childrenContainerRef.current.scrollHeight;
    childrenContainerRef.current.style.maxHeight = pxToRem(scrollHeight);
  };
  var closeChildrenContainer = function closeChildrenContainer() {
    if (!childrenContainerRef.current) {
      return;
    }
    childrenContainerRef.current.style.maxHeight = pxToRem(0);
  };
  var handleChildClick = function handleChildClick() {
    if (minimized) {
      closeMenu();
    }
  };
  useLayoutEffect(function () {
    if (collapsibleOpen) {
      openChildrenContainer();
    } else {
      closeChildrenContainer();
    }
  }, [collapsibleOpen, children]);
  /**
   * If collapsible is open and the sidebar gets minimized, close the collapsible.
   */
  useLayoutEffect(function () {
    if (collapsibleOpen && minimized && !previousMinimized) {
      closeChildrenContainer();
      closeCollapsible();
    }
  }, [collapsibleOpen, minimized, previousMinimized]);
  /**
   * If the sidebar goes from minimized to expanded and the collapsible is open, open the children container.
   */
  useLayoutEffect(function () {
    if (!minimized && open) {
      setCollapsibleOpen(true);
    }
  }, [minimized, open]);
  var adornmentRight = collapsibleOpen ? /*#__PURE__*/jsx(DO_NOT_USE_FaWrapper, {
    $size: 13,
    icon: faChevronUp
  }) : /*#__PURE__*/jsx(DO_NOT_USE_FaWrapper, {
    $size: 13,
    icon: faChevronDown
  });
  var value = {
    open: collapsibleOpen,
    mode: minimized ? 'menu' : 'sidebar',
    onChildClick: handleChildClick
  };
  return /*#__PURE__*/jsx(SidebarMenuCollapsibleContext.Provider, {
    value: value,
    children: /*#__PURE__*/jsxs(Box, {
      flexDirection: "column",
      ref: refs.setReference,
      children: [/*#__PURE__*/jsx(SidebarMenuItem, {
        icon: icon,
        label: label,
        active: activeChild,
        onClick: toggleChildren,
        adornmentRight: adornmentRight
      }), /*#__PURE__*/jsxs(SidebarMenuCollapsibleChildrenContainer, {
        ref: childrenContainerRef,
        $open: collapsibleOpen,
        children: [/*#__PURE__*/jsx(GapFix, {}), children]
      }), /*#__PURE__*/createPortal( /*#__PURE__*/jsx(RemoveScroll, {
        enabled: isMobile && menuOpen,
        children: /*#__PURE__*/jsx(MenuContainer, {
          role: "menu",
          ref: refs.setFloating,
          style: menuContainerStyle,
          $state: menuTransitionState,
          children: /*#__PURE__*/jsx(FocusLock, {
            group: menuContentId,
            disabled: !menuOpen,
            shards: [refs.reference],
            children: /*#__PURE__*/jsx(MenuList, {
              children: children
            })
          })
        })
      }), menuRoot)]
    })
  });
}

export { SidebarMenuCollapsible, SidebarMenuCollapsibleContext };
